import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Typography, Paper, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InvoiceSettings from 'components/manage-invoices/create-invoice-step-2/invoice-settings/invoice-settings.component';
import InvoiceCompilation from 'components/manage-invoices/create-invoice-step-2/invoice-compilation/invoice-compilation.component';
import { useMutation, useQuery } from '@apollo/client/react';
import { createCollectiveInvoicesTemplate, getOrdersForCollectiveInvoiceTemplate } from 'apollo/templates/invoices';
import Loader from 'components/shared/loader/loader.component';
import {
    ACCOUNTING_PROVIDER_LABEL,
    AccountingProvider,
    isUserFrontendFeedbackError,
    useDenthubErrorBoundary,
} from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { invoiceDateVar, selectedClinicsVar } from 'apollo/reactive-variables/invoice';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import {
    CollectiveInvoice,
    CollectiveInvoiceSummary,
    InvoiceDelivery,
    InvoiceLineItem,
    InvoiceNote,
    InvoiceType,
} from 'models/invoice';
import { useNavigate } from 'react-router-dom';
import { ApolloCache, ApolloError, Reference } from '@apollo/client';
import { Order } from 'models/order';
import MissingCustomerNumberDialog from '../missing-customer-number-dialog/missing-customer-number-dialog.component';
import ErrorDialog from 'components/shared/error-dialog/error-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { groupBy } from 'lodash';

type InvoiceSettingAndCompilationProps = {
    handleBack: () => void;
    isPtjOrg: boolean;
};

/** A `failedInvoices` row on the created batch; rejected invoices have no invoiceId. */
type RejectedBatchInvoice = {
    invoiceId?: string | null;
    invoiceNumber?: string | null;
    clinicName?: string | null;
    errorMessage: string;
    provider: AccountingProvider;
};

const InvoiceSettingAndCompilation: FC<InvoiceSettingAndCompilationProps> = ({
    handleBack,
    isPtjOrg,
}: InvoiceSettingAndCompilationProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const navigate = useNavigate();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentOrganization = currentOrgVar();
    const [invoiceSummaryMap, setInvoiceSummaryMap] = useState<{ [key: string]: CollectiveInvoice }>({});
    const [orderPrice, setOrderPrice] = useState<{ [key: string]: number }>({});
    const [totalPrice, setTotalPrice] = useState<Record<string, number>>({});
    const [shouldSendEmails, setShouldSendEmails] = useState(true);
    const [invoiceWithEmailCount, setInvoiceWithEmailCount] = useState(0);
    const [clinicsWithoutCustomerNumber, setClinicsWithoutCustomerNumber] = useState<string[]>([]);
    const [showMissingCustomerNumberDialog, setShowMissingCustomerNumberDialog] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [apolloError, setApolloError] = useState<ApolloError | null>(null);
    const [invoiceSummary, setInvoiceSummary] = useState<CollectiveInvoiceSummary[]>([]);
    const [uncheckedLineItems, setUncheckedLineItems] = useState<InvoiceLineItem[]>([]);
    const [disabledInvoiceKeys, setDisabledInvoiceKeys] = useState<Set<string>>(new Set());
    const [progress, setProgress] = useState(0);
    const { showBoundary, showUserFrontendFeedback } = useDenthubErrorBoundary();

    const getLineItemMap = (invoiceSummary: CollectiveInvoiceSummary) => {
        return invoiceSummary.lineItems.reduce((res, lineItem: InvoiceLineItem, index: number) => {
            res.set(index + 1, lineItem);
            return res;
        }, new Map<number, InvoiceLineItem>());
    };

    const getCollectiveInvoiceFromOrders = (invoicesSummary: CollectiveInvoiceSummary[]) => {
        return invoicesSummary.reduce(
            (obj: { [key: string]: CollectiveInvoice }, summary: CollectiveInvoiceSummary) => {
                return Object.assign({}, obj, {
                    [summary.key]: {
                        clinic: summary.clinic,
                        clinicName: summary.clinicName,
                        ptjCustomerNumber: summary.ptjCustomerNumber,
                        dentist: summary.dentist,
                        dentistName: summary.dentistName,
                        invoiceDate: invoiceDateVar().invoiceDate,
                        paymentTerm: summary.paymentTerm,
                        invoiceNumber: '',
                        deliveryMethod: summary.deliveryMethod,
                        isEmailSetForDelivery: summary.emails.length > 0,
                        currency: summary.currency,
                        lineItems: Array.from(summary.lineItemMap?.values() || []),
                    },
                });
            },
            {},
        );
    };

    const unCheckOrderItem = (
        invoicesSummaryMap: {
            [key: string]: CollectiveInvoice;
        },
        price: {
            [key: string]: number;
        },
        orderId: string,
        invoiceKey: string,
    ) => {
        const uncheckedItems = invoicesSummaryMap[invoiceKey].lineItems.filter((lineItem: InvoiceLineItem) => {
            return (lineItem.order as Order)?._id === orderId;
        });
        const remainingItems = invoicesSummaryMap[invoiceKey].lineItems.filter((lineItem: InvoiceLineItem) => {
            return (lineItem.order as Order)?._id !== orderId;
        });
        const total = uncheckedItems.reduce((acc, item) => {
            return acc + item.totalIncVat;
        }, 0);
        price[invoiceKey] = price[invoiceKey] - total;
        setUncheckedLineItems([...uncheckedLineItems, ...uncheckedItems]);
        invoicesSummaryMap[invoiceKey].lineItems = remainingItems;
        setInvoiceSummaryMap(invoicesSummaryMap);
    };

    const checkOrderItem = (
        invoicesSummaryMap: {
            [key: string]: CollectiveInvoice;
        },
        price: {
            [key: string]: number;
        },
        orderId: string,
        invoiceKey: string,
    ) => {
        const unUsedLineItems = [...uncheckedLineItems];
        const checkedItems = unUsedLineItems.filter((lineItem: InvoiceLineItem) => {
            return (lineItem.order as Order)?._id === orderId;
        });
        const total = checkedItems.reduce((acc, item) => {
            return acc + item.totalIncVat;
        }, 0);
        price[invoiceKey] = price[invoiceKey] + total;
        invoicesSummaryMap[invoiceKey].lineItems = [...invoicesSummaryMap[invoiceKey].lineItems, ...checkedItems];
        setInvoiceSummaryMap(invoicesSummaryMap);
        const remainingItems = unUsedLineItems.filter((lineItem: InvoiceLineItem) => {
            return (lineItem.order as Order)?._id !== orderId;
        });
        setUncheckedLineItems(remainingItems);
    };

    const unCheckNoteItem = (
        invoicesSummaryMap: {
            [key: string]: CollectiveInvoice;
        },
        price: {
            [key: string]: number;
        },
        noteId: string,
        invoiceKey: string,
    ) => {
        const itemIndex = invoicesSummaryMap[invoiceKey].lineItems.findIndex((lineItem: InvoiceLineItem) => {
            return (lineItem.note as InvoiceNote)?._id === noteId;
        });
        if (itemIndex > -1) {
            price[invoiceKey] = price[invoiceKey] - invoicesSummaryMap[invoiceKey].lineItems[itemIndex].totalIncVat;
            setUncheckedLineItems([...uncheckedLineItems, invoicesSummaryMap[invoiceKey].lineItems[itemIndex]]);
            invoicesSummaryMap[invoiceKey].lineItems.splice(itemIndex, 1);
            setInvoiceSummaryMap(invoicesSummaryMap);
        }
    };

    const checkNoteItem = (
        invoicesSummaryMap: {
            [key: string]: CollectiveInvoice;
        },
        price: {
            [key: string]: number;
        },
        noteId: string,
        invoiceKey: string,
    ) => {
        const unUsedLineItems = [...uncheckedLineItems];
        const itemIndex = unUsedLineItems.findIndex((lineItem) => {
            return (lineItem.note as InvoiceNote)?._id === noteId;
        });
        if (itemIndex > -1) {
            price[invoiceKey] = price[invoiceKey] + unUsedLineItems[itemIndex].totalIncVat;
            invoicesSummaryMap[invoiceKey].lineItems.push(unUsedLineItems[itemIndex]);
            setInvoiceSummaryMap(invoicesSummaryMap);
            unUsedLineItems.splice(itemIndex, 1);
            setUncheckedLineItems(unUsedLineItems);
        }
    };

    const handleLineItemSelection = (invoiceKey: string, isChecked: boolean, orderId?: string, noteId?: string) => {
        const invoicesSummaryMap: {
            [key: string]: CollectiveInvoice;
        } = JSON.parse(JSON.stringify(invoiceSummaryMap));
        const price = { ...orderPrice };
        const total = { ...totalPrice };
        total[invoiceSummaryMap[invoiceKey].currency] =
            total[invoiceSummaryMap[invoiceKey].currency] - price[invoiceKey];
        if (!isChecked) {
            if (orderId) {
                unCheckOrderItem(invoicesSummaryMap, price, orderId, invoiceKey);
            } else if (noteId) {
                unCheckNoteItem(invoicesSummaryMap, price, noteId, invoiceKey);
            }
        } else {
            if (orderId) {
                checkOrderItem(invoicesSummaryMap, price, orderId, invoiceKey);
            } else if (noteId) {
                checkNoteItem(invoicesSummaryMap, price, noteId, invoiceKey);
            }
        }
        setOrderPrice(price);
        total[invoiceSummaryMap[invoiceKey].currency] =
            total[invoiceSummaryMap[invoiceKey].currency] + price[invoiceKey];
        setTotalPrice(total);
    };

    const [createCollectiveInvoices, { loading: isInvoiceCreationInProgress }] = useMutation(
        createCollectiveInvoicesTemplate,
        {
            onCompleted: ({ createCollectiveInvoices: invoiceBatch }) => {
                // Invoices Fortnox/Visma refused are on the batch without an invoiceId. The rest of
                // the batch is running, so tell the user what to fix before showing the batch page.
                const rejected: RejectedBatchInvoice[] = (invoiceBatch.failedInvoices ?? []).filter(
                    (failed: RejectedBatchInvoice) => !failed.invoiceId && !!failed.provider,
                );
                if (rejected.length > 0) {
                    const provider = ACCOUNTING_PROVIDER_LABEL[rejected[0].provider] ?? rejected[0].provider;
                    showUserFrontendFeedback({
                        translationTitleKey:
                            rejected.length > 1 ? 'ACCOUNTING_REJECTED_TITLE_MANY' : 'ACCOUNTING_REJECTED_TITLE',
                        translationMessageKey: 'ACCOUNTING_REJECTED_PARTIAL_MESSAGE',
                        translationParams: { provider, count: rejected.length },
                        rejections: rejected.map((failed) => ({
                            provider: failed.provider,
                            message: failed.errorMessage,
                            customerName: failed.clinicName ?? undefined,
                            providerInvoiceNumber: failed.invoiceNumber ?? undefined,
                        })),
                    });
                }
                navigate('/invoices/done', { state: { batchId: invoiceBatch._id } });
            },
            onError(error: ApolloError) {
                console.error('Error while creating collective invoice batch: ', error);
                // No GraphQL error = transport failure (error page); user feedback = the
                // accounting system's own message, shown in the feedback dialog.
                if (error.graphQLErrors.length === 0 || isUserFrontendFeedbackError(error)) {
                    showBoundary(error);
                    return;
                }
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('invoices:INVOICE_BATCH_FAILED'),
                });
            },
            update(cache: ApolloCache<unknown>) {
                const noteIdsToRemove = Object.values(invoiceSummaryMap).reduce((agg: string[], summary) => {
                    const notes: string[] = summary.lineItems
                        .filter((lineItem) => !!lineItem.note)
                        .map((lineItem) =>
                            typeof lineItem.note === 'string' ? lineItem.note : (lineItem.note?._id as string),
                        );
                    return agg.concat(notes);
                }, []);
                cache.modify({
                    fields: {
                        invoiceNotes(existingInvoiceNoteRefs: Reference | (Reference | undefined)[], { readField }) {
                            if (existingInvoiceNoteRefs instanceof Array) {
                                return [...existingInvoiceNoteRefs].filter((invoiceNoteRef: Reference | undefined) => {
                                    return noteIdsToRemove.every(
                                        (invoiceNoteId) => invoiceNoteId !== readField('_id', invoiceNoteRef),
                                    );
                                });
                            }
                            return [];
                        },
                    },
                });
            },
        },
    );

    const { loading } = useQuery(getOrdersForCollectiveInvoiceTemplate, {
        //INFO: No need to store this data in the cache as the order data can be changed by many mutations
        fetchPolicy: 'cache-and-network',
        variables: {
            clinics: Object.keys(selectedClinicsVar()),
            ordersToDate: invoiceDateVar().orderToDate,
        },
        onError(error) {
            setShowErrorDialog(true);
            setApolloError(error);
        },
        onCompleted: async ({ ordersForCollectiveInvoice }) => {
            const invoicesSummary = [...ordersForCollectiveInvoice].map((summary: CollectiveInvoiceSummary) => {
                const key =
                    summary.invoiceType === InvoiceType.COLLECTIVE_DENTIST
                        ? `${summary.clinic}::${summary.dentist}`
                        : summary.clinic;
                const displayLabel =
                    summary.invoiceType === InvoiceType.COLLECTIVE_DENTIST
                        ? `${summary.clinicName} - ${summary.dentistName}`
                        : summary.clinicName;
                setOrderPrice(Object.assign(orderPrice, { [key]: summary.totalPrice }));
                const lineItemMap = getLineItemMap(summary);
                return {
                    ...summary,
                    key,
                    displayLabel,
                    invoiceDate: invoiceDateVar().invoiceDate,
                    invoiceNumber: '',
                    lineItemMap,
                    lineItems: Array.from(lineItemMap?.values() || []),
                };
            });
            const summaryMap = getCollectiveInvoiceFromOrders(invoicesSummary);
            setInvoiceSummary(invoicesSummary);
            let count = 0;
            const groupedInvoices = groupBy(
                ordersForCollectiveInvoice,
                (invoice: CollectiveInvoiceSummary) => invoice.currency,
            );
            const total: Record<string, number> = {};
            Object.keys(groupedInvoices).forEach((currency: string) => {
                total[currency] = groupedInvoices[currency].reduce((sum: number, invoice: CollectiveInvoiceSummary) => {
                    if (invoice.deliveryMethod === 'EMAIL') {
                        count++;
                    }
                    return sum + invoice.totalPrice;
                }, 0);
            });
            setInvoiceWithEmailCount(count);
            setTotalPrice(total);
            setInvoiceSummaryMap(summaryMap);
        },
    });

    const handleClick = () => {
        handleBack();
    };

    const checkIfAllClinicsHaveCustomerNum = () => {
        return Object.values(invoiceSummaryMap)
            .filter((summary: CollectiveInvoice) => {
                return !summary.ptjCustomerNumber;
            })
            .map((summary: CollectiveInvoice) => {
                return summary.clinicName;
            });
    };

    const isDisabled = () => {
        const enabledInvoices = Object.entries(invoiceSummaryMap)
            .filter(([key]) => !disabledInvoiceKeys.has(key))
            .map(([, invoice]) => invoice);
        if (!enabledInvoices.length) {
            return true;
        }
        const isMissingInvoiceNumber = enabledInvoices.some((invoice) => !invoice.invoiceNumber);
        if (isManualInvoiceNumber() && isMissingInvoiceNumber) {
            return true;
        }
        if (enabledInvoices.some((invoice) => !invoice.lineItems.length)) {
            return true;
        }
        const enabledPrices = Object.entries(orderPrice)
            .filter(([key]) => !disabledInvoiceKeys.has(key))
            .map(([, price]) => price);
        if (enabledPrices.some((price) => price <= 0)) {
            return true;
        }
    };

    const createInvoice = () => {
        if (isPtjOrg) {
            const clinicsWithoutCustomerNumber = checkIfAllClinicsHaveCustomerNum();
            if (clinicsWithoutCustomerNumber.length) {
                // open dialog here
                setClinicsWithoutCustomerNumber(clinicsWithoutCustomerNumber);
                setShowMissingCustomerNumberDialog(true);
                return;
            }
        }
        type EnabledSummary = CollectiveInvoice & {
            dentistName?: string;
            isEmailSetForDelivery?: boolean;
            deliveryMethod?: string;
        };
        let isEmailNotSet = false;
        const enabledSummaries: EnabledSummary[] = Object.entries(invoiceSummaryMap)
            .filter(([key]) => !disabledInvoiceKeys.has(key))
            .map(([, summary]) => summary as EnabledSummary);
        const invoices = enabledSummaries.map((summary) => {
            if (summary.deliveryMethod === InvoiceDelivery.EMAIL && !summary.isEmailSetForDelivery) {
                isEmailNotSet = true;
            }
            return {
                clinic: summary.clinic,
                dentist: summary.dentist,
                dentistName: summary.dentistName,
                invoiceDate: summary.invoiceDate,
                paymentTerm: summary.paymentTerm,
                currency: summary.currency,
                invoiceNumber: summary.invoiceNumber,
                orders: Array.from(
                    new Set(
                        summary.lineItems
                            .filter((lineItem) => !!lineItem.order)
                            .map((lineItem) => (lineItem.order as Order)?._id),
                    ).values(),
                ),
                lineItems: summary.lineItems.map((lineItem) => {
                    const order = lineItem.order as Order;
                    return {
                        description: lineItem.description,
                        itemPrice: lineItem.itemPrice,
                        quantity: lineItem.quantity,
                        itemVatPercentage: lineItem.itemVatPercentage,
                        vatTreatment: lineItem.vatTreatment,
                        itemVat: lineItem.itemVat,
                        totalIncVat: lineItem.totalIncVat,
                        isIdentifyingRow: lineItem.isIdentifyingRow,
                        order: order?._id,
                        articleNo: order?.orderId,
                        isCertified: order?.isCertified,
                        certifiedBy:
                            order?.certified?.by &&
                            `${order.certified.by.name.firstName} ${order.certified.by.name.lastName}`,
                        note: (lineItem.note as InvoiceNote)?._id,
                    };
                }),
            };
        });
        if (isEmailNotSet) {
            setShowErrorDialog(true);
            setApolloError(new ApolloError({ errorMessage: translate('invoices:INVOICE_EMAIL_NOT_SET') }));
            return;
        }
        createCollectiveInvoices({
            variables: { invoices, shouldSendEmails: invoiceWithEmailCount > 0 ? shouldSendEmails : false },
        });
    };

    const onPaymentTermChange = (paymentTerm: number, invoiceKey: string) => {
        const invoices = { ...invoiceSummaryMap };
        invoices[invoiceKey].paymentTerm = paymentTerm;
        setInvoiceSummaryMap(invoices);
    };

    const onInvoiceNumberChange = (invoiceNumber: string, invoiceKey: string) => {
        const invoices = { ...invoiceSummaryMap };
        invoices[invoiceKey].invoiceNumber = invoiceNumber;
        setInvoiceSummaryMap(invoices);
    };

    const onLineItemsChange = (invoiceKey: string, lineItem?: InvoiceLineItem, id?: number) => {
        const summaryMap: {
            [key: string]: CollectiveInvoice;
        } = { ...invoiceSummaryMap };
        const price = { ...orderPrice };
        const total = { ...totalPrice };
        total[summaryMap[invoiceKey].currency] = total[summaryMap[invoiceKey].currency] - price[invoiceKey];
        if (id) {
            const itemIndex = summaryMap[invoiceKey].lineItems.findIndex((item) => item.id === id);
            if (itemIndex > -1) {
                price[invoiceKey] = price[invoiceKey] - summaryMap[invoiceKey].lineItems[itemIndex].totalIncVat;
                summaryMap[invoiceKey].lineItems.splice(itemIndex, 1);
            }
        } else if (lineItem) {
            const itemIndex = summaryMap[invoiceKey].lineItems.findIndex((item) => item.id === lineItem.id);
            if (itemIndex > -1) {
                price[invoiceKey] =
                    price[invoiceKey] - summaryMap[invoiceKey].lineItems[itemIndex].totalIncVat + lineItem.totalIncVat;
                summaryMap[invoiceKey].lineItems.splice(itemIndex, 1, lineItem);
            } else {
                price[invoiceKey] = price[invoiceKey] + lineItem.totalIncVat;
                summaryMap[invoiceKey].lineItems.push(lineItem);
            }
        }
        setOrderPrice(price);
        total[summaryMap[invoiceKey].currency] = total[summaryMap[invoiceKey].currency] + price[invoiceKey];
        setTotalPrice(total);
        setInvoiceSummaryMap(summaryMap);
    };

    const handleInvoiceToggle = (invoiceKey: string, isEnabled: boolean) => {
        const updatedDisabledKeys = new Set(disabledInvoiceKeys);
        if (isEnabled) {
            updatedDisabledKeys.delete(invoiceKey);
        } else {
            updatedDisabledKeys.add(invoiceKey);
        }
        const total: Record<string, number> = {};
        Object.entries(orderPrice).forEach(([key, price]) => {
            if (!updatedDisabledKeys.has(key)) {
                const invoiceCurrency = invoiceSummaryMap[key]?.currency;
                if (invoiceCurrency) {
                    total[invoiceCurrency] = (total[invoiceCurrency] || 0) + price;
                }
            }
        });
        setDisabledInvoiceKeys(updatedDisabledKeys);
        setTotalPrice(total);
    };

    const handleEmailCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShouldSendEmails(event.target.checked);
    };

    const isManualInvoiceNumber = () => {
        return currentOrganization?.invoicing?.configuration?.invoiceNumberType === 'MANUAL';
    };

    useEffect(() => {
        setProgress(0);
        const timer = setInterval(() => {
            setProgress((state) => state + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, [isInvoiceCreationInProgress]);

    const currentProgress = () => {
        const noOfInvoices = Object.keys(invoiceSummaryMap).length;
        return (progress / noOfInvoices) * 100;
    };

    if (loading) {
        return <Loader />;
    } else if (isInvoiceCreationInProgress) {
        return (
            <Box mt={4}>
                <LinearProgress variant="determinate" value={currentProgress()} />
            </Box>
        );
    }

    return (
        <Fragment>
            <Box p={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        {invoiceSummary.length ? (
                            <InvoiceSettings
                                priceMap={orderPrice}
                                handleLineItemSelection={handleLineItemSelection}
                                onPaymentTermChange={onPaymentTermChange}
                                onInvoiceNumberChange={onInvoiceNumberChange}
                                onLineItemsChange={onLineItemsChange}
                                onInvoiceToggle={handleInvoiceToggle}
                                disabledInvoiceKeys={disabledInvoiceKeys}
                                invoices={invoiceSummary}
                            />
                        ) : (
                            <Typography variant="h5">{translate('orders:NO_ORDERS_TO_INVOICE')}</Typography>
                        )}
                        {!isInvoiceCreationInProgress ? (
                            <Box mt={2}>
                                <Button onClick={handleClick} size="large">
                                    {translate('common:BACK')}
                                </Button>
                            </Box>
                        ) : undefined}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {invoiceSummary.length ? <InvoiceCompilation totalValue={totalPrice} /> : undefined}
                        {!isInvoiceCreationInProgress ? (
                            <Box mt={2}>
                                {invoiceSummary.length ? (
                                    <Fragment>
                                        {invoiceWithEmailCount > 0 ? (
                                            <Box mb={2}>
                                                <Paper square variant="outlined">
                                                    <Box p={2}>
                                                        <Alert severity="info">
                                                            {`${invoiceWithEmailCount} ${
                                                                invoiceWithEmailCount > 1
                                                                    ? translate('common:CLINICS').toLowerCase()
                                                                    : translate('common:CLINIC').toLowerCase()
                                                            } ${translate('invoices:EMAIL_CONFIG_TEXT')}`}
                                                        </Alert>
                                                        <Box></Box>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    color="secondary"
                                                                    name="email-invoice"
                                                                    checked={shouldSendEmails}
                                                                    onChange={handleEmailCheckbox}
                                                                />
                                                            }
                                                            label={translate('invoices:SEND_EMAIL_AUTOMATICALLY')}
                                                        />
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        ) : undefined}
                                        <Button
                                            disabled={isDisabled()}
                                            onClick={createInvoice}
                                            size="large"
                                            fullWidth
                                            variant="contained"
                                        >
                                            {translate('common:CREATE')}{' '}
                                            {invoiceSummary.filter((inv) => !disabledInvoiceKeys.has(inv.key)).length}{' '}
                                            {invoiceSummary.filter((inv) => !disabledInvoiceKeys.has(inv.key))
                                                .length === 1
                                                ? translate('orders:INVOICE')
                                                : translate('orders:INVOICES')}
                                        </Button>
                                    </Fragment>
                                ) : undefined}
                            </Box>
                        ) : undefined}
                    </Grid>
                </Grid>
            </Box>
            <MissingCustomerNumberDialog
                clinicsWithoutCustomerNumber={clinicsWithoutCustomerNumber}
                open={showMissingCustomerNumberDialog}
                onClose={() => {
                    setShowMissingCustomerNumberDialog(false);
                }}
            />
            <ErrorDialog
                errorMessage={apolloError?.message || ''}
                extraInfo={(apolloError?.graphQLErrors[0]?.extensions?.exception as any)?.otherInfo || {}}
                open={showErrorDialog}
                onClose={() => {
                    setShowErrorDialog(false);
                }}
            />
        </Fragment>
    );
};

export default InvoiceSettingAndCompilation;
